// src/components/auth/AuthModal.tsx - TypeScript version
import React, { useState, useEffect, FormEvent } from 'react';
import { useModalA11y } from '../UI/useModalA11y';
import { X, BookOpen, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.ts';
import { useTheme } from '../../context/ThemeContext.tsx';
import { trackSignupStarted, trackSignupCompleted } from '../../lib/services/analytics/posthogService';
import SignupSuccessView from './SignupSuccessView';
import AuthFormFields from './AuthFormFields';

type AuthMode = 'signin' | 'signup' | 'reset' | 'newpassword';
type NotificationType = 'success' | 'error' | 'info';

interface FormData {
  email: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  acceptedAiConsent: boolean;
}

interface Notification {
  message: string;
  type: NotificationType;
}

const AuthModal: React.FC = () => {
  const { showAuthModal, closeAuthModal, signIn, signUp, resetPassword, updatePassword, authModalMode } = useAuth();
  const { darkMode } = useTheme();
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    acceptedTerms: false,
    acceptedPrivacy: false,
    acceptedAiConsent: false,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
  const [signupEmail, setSignupEmail] = useState<string>('');

  // Dialog a11y (focus trap, Escape-to-close, focus restore) for the modal card
  // below. Declared here — above the `!showAuthModal` early return — so the hook
  // is called unconditionally. handleClose is referenced lazily (only invoked on
  // Escape/unmount), so its later declaration is fine.
  const dialogRef = useModalA11y(() => handleClose());

  // Sync auth modal mode from context (e.g., password recovery)
  useEffect(() => {
    if (authModalMode) {
      setAuthMode(authModalMode);
    }
  }, [authModalMode]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showAuthModal) {
      document.body.style.overflow = 'hidden';
      // Add viewport meta tag for mobile if it doesn't exist
      const viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        document.head.appendChild(meta);
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAuthModal]);

  const showNotification = (message: string, type: NotificationType = 'success'): void => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Prevent double submission
    if (loading) return;

    // Basic validation
    if (authMode === 'newpassword') {
      if (!formData.newPassword) {
        showNotification('Please enter your new password.', 'error');
        return;
      }
      if (formData.newPassword.length < 6) {
        showNotification('Password must be at least 6 characters.', 'error');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        showNotification('Passwords do not match.', 'error');
        return;
      }
    } else {
      if (!formData.email) {
        showNotification('Please enter your email address.', 'error');
        return;
      }

      if (authMode !== 'reset' && !formData.password) {
        showNotification('Please enter your password.', 'error');
        return;
      }

      if (authMode !== 'reset' && formData.password.length < 6) {
        showNotification('Password must be at least 6 characters.', 'error');
        return;
      }
    }

    // Additional validation for signup - require terms acceptance
    if (authMode === 'signup' && !formData.acceptedTerms) {
      showNotification('Please accept the Terms of Service to continue.', 'error');
      return;
    }

    if (authMode === 'signup' && !formData.acceptedPrivacy) {
      showNotification('Please accept the Privacy Policy to continue.', 'error');
      return;
    }

    if (authMode === 'signup' && !formData.acceptedAiConsent) {
      showNotification('Please acknowledge AI data processing to continue.', 'error');
      return;
    }

    setLoading(true);

    try {
      let result;
      if (authMode === 'newpassword') {
        result = await updatePassword(formData.newPassword);
        if (!result.error) {
          showNotification('Password updated successfully! You are now signed in.', 'success');
          setFormData(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          const errorMessage = result.error.message ?? String(result.error);
          showNotification(errorMessage, 'error');
        }
      } else if (authMode === 'reset') {
        result = await resetPassword(formData.email);
        if (!result.error) {
          showNotification(
            'Password reset email sent! Check your inbox and click the link to reset your password. The link expires in 60 minutes.',
            'success'
          );
          // Don't clear email or switch modes immediately - let user see success state
          setFormData(prev => ({ ...prev, password: '', newPassword: '', confirmPassword: '' }));
          // Stay on reset mode to show success state and instructions
        } else {
          const errorMessage = result.error.message ?? result.error;
          showNotification(errorMessage, 'error');
        }
      } else if (authMode === 'signup') {
        trackSignupStarted();
        result = await signUp(formData.email, formData.password, {
          ai_consent_at: new Date().toISOString(),
        });

        if (result?.user) {
          trackSignupCompleted();
          // Show success screen instead of tiny notification
          setSignupEmail(formData.email);
          setSignupSuccess(true);
          // Keep email but clear passwords
          setFormData(prev => ({ ...prev, password: '', newPassword: '', confirmPassword: '' }));
        }
        // Note: signup errors are thrown by authService (not returned),
        // so they're handled in the catch block below.
      } else {
        result = await signIn(formData.email, formData.password);
        // signIn throws on error, so if we reach here it succeeded
        showNotification('Welcome back!', 'success');
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Auth error:', error);

      // signUp/signIn throw errors (not returned), so they arrive here.
      // Surface user-friendly messages for known error types.
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes('User already registered')) {
        showNotification(
          'An account with this email already exists. Try signing in instead, or reset your password if you forgot it.',
          'info'
        );
        setTimeout(() => setAuthMode('signin'), 3000);
      } else if (errorMessage.includes('Invalid login credentials')) {
        showNotification(
          'Incorrect email or password. If you forgot your password, use the "Forgot Password?" link below.',
          'error'
        );
      } else if (errorMessage.includes('Email not confirmed')) {
        showNotification(
          'Please check your email and click the verification link to activate your account. Check your spam folder too!',
          'error'
        );
      } else if (errorMessage.includes('Too Many Requests') || errorMessage.includes('429')) {
        showNotification('Too many attempts. Please wait a minute and try again.', 'error');
      } else if (errorMessage.includes('For security purposes')) {
        const match = errorMessage.match(/after (\d+) seconds/);
        const seconds = match ? match[1] : '60';
        showNotification(
          `For security, please wait ${seconds} seconds before trying again.`,
          'error'
        );
      } else if (errorMessage.includes('Password should be')) {
        showNotification(errorMessage, 'error');
      } else {
        showNotification(errorMessage || 'An unexpected error occurred. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (): void => {
    closeAuthModal();
    setFormData({
      email: '',
      password: '',
      newPassword: '',
      confirmPassword: '',
      acceptedTerms: false,
      acceptedPrivacy: false,
      acceptedAiConsent: false,
    });
    setAuthMode('signin');
    setNotification(null);
    setLoading(false);
    setSignupSuccess(false);
    setSignupEmail('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleResendEmail = async (): Promise<void> => {
    setLoading(true);
    try {
      await resetPassword(signupEmail);
      showNotification('Verification email resent!', 'success');
    } catch (_error) {
      showNotification('Failed to resend email. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSignIn = (): void => {
    setSignupSuccess(false);
    setAuthMode('signin');
  };

  if (!showAuthModal) return null;

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Please wait...';
  } else if (authMode === 'signin') {
    submitButtonLabel = 'Sign In';
  } else if (authMode === 'signup') {
    submitButtonLabel = 'Create Account';
  } else if (authMode === 'reset') {
    submitButtonLabel = 'Send Reset Email';
  } else {
    submitButtonLabel = 'Set Password';
  }

  const notificationSuccessBg = darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-800';
  const notificationErrorBg = darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800';
  const notificationDefaultBg = darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-800';
  const notificationBgMap: Record<string, string> = { success: notificationSuccessBg, error: notificationErrorBg };
  const notificationBgClass = notificationBgMap[notification?.type ?? ''] ?? notificationDefaultBg;

  // Determine modal title
  let modalTitle: string;
  if (authMode === 'signin') {
    modalTitle = 'Welcome Back';
  } else if (authMode === 'signup') {
    modalTitle = 'Join Life Re:Scripted';
  } else if (authMode === 'reset') {
    modalTitle = 'Reset Password';
  } else {
    modalTitle = 'Set New Password';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        tabIndex={-1}
        className={`relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
        }`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-6 h-6 text-orange-500" />
              <h2
                id="auth-modal-title"
                className={`text-xl font-bold ${darkMode ? 'text-orange-100' : 'text-slate-800'}`}
              >
                {modalTitle}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                  : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Signup Success Screen */}
          {authMode === 'signup' && signupSuccess ? (
            <SignupSuccessView
              signupEmail={signupEmail}
              loading={loading}
              onResendEmail={handleResendEmail}
              onGoToSignIn={handleGoToSignIn}
            />
          ) : (
            <AuthFormFields
              authMode={authMode}
              formData={formData}
              loading={loading}
              submitButtonLabel={submitButtonLabel}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
            />
          )}

          {/* Mode Switching - Hide when showing success screen */}
          {!(authMode === 'signup' && signupSuccess) && (
          <div className="mt-6 text-center">
            {authMode === 'signin' && (
              <div className="space-y-2">
                <button
                  onClick={() => setAuthMode('signup')}
                  className={`text-sm underline transition-colors ${
                    darkMode
                      ? 'text-orange-300 hover:text-orange-200'
                      : 'text-orange-600 hover:text-orange-700'
                  }`}
                >
                  Need an account? Sign up
                </button>
                <br />
                <button
                  onClick={() => setAuthMode('reset')}
                  className={`text-sm underline transition-colors ${
                    darkMode
                      ? 'text-slate-400 hover:text-slate-300'
                      : 'text-slate-600 hover:text-slate-700'
                  }`}
                >
                  Forgot password?
                </button>
              </div>
            )}
            {authMode === 'signup' && (
              <button
                onClick={() => setAuthMode('signin')}
                className={`text-sm underline transition-colors ${
                  darkMode
                    ? 'text-orange-300 hover:text-orange-200'
                    : 'text-orange-600 hover:text-orange-700'
                }`}
              >
                Already have an account? Sign in
              </button>
            )}
            {authMode === 'newpassword' && (
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Choose a strong password with at least 6 characters.
              </p>
            )}
            {authMode === 'reset' && (
              <div className="space-y-3">
                {notification?.type === 'success' && (
                  <div
                    className={`p-4 rounded-lg text-sm ${
                      darkMode
                        ? 'bg-green-900/30 text-green-200 border border-green-700'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Email Sent Successfully!</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <p>Check your inbox (and spam folder)</p>
                      <p>Click the reset link within 60 minutes</p>
                      <p>Return here to sign in with your new password</p>
                    </div>
                  </div>
                )}
                <div className="flex space-x-4 justify-center">
                  <button
                    onClick={() => setAuthMode('signin')}
                    className={`text-sm underline transition-colors ${
                      darkMode
                        ? 'text-orange-300 hover:text-orange-200'
                        : 'text-orange-600 hover:text-orange-700'
                    }`}
                  >
                    Back to Sign In
                  </button>
                  {notification?.type === 'success' && (
                    <button
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          password: '',
                          newPassword: '',
                          confirmPassword: '',
                        }));
                        setNotification(null);
                        // Trigger another reset email
                        handleSubmit(new Event('submit') as unknown as React.FormEvent<HTMLFormElement>);
                      }}
                      className={`text-sm underline transition-colors ${
                        darkMode
                          ? 'text-slate-400 hover:text-slate-300'
                          : 'text-slate-600 hover:text-slate-700'
                      }`}
                    >
                      Resend Email
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Notification - Hide when showing success screen (has its own messaging) */}
        {!(authMode === 'signup' && signupSuccess) && notification && (
          <div
            className={`px-6 py-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
          >
            <div
              className={`flex items-center space-x-3 p-3 rounded-lg ${notificationBgClass}`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0" />}
              {notification.type === 'error' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
              {notification.type === 'info' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
              <p className="text-sm">{notification.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
